// Condição comercial de uma negociação, resolvida para o export do CRM.
//
// O CRM só carimba publico/plano_codigo/multiplicador_* desde a virada de
// jul/2026 — em ago/2026 são 322 negociações de 3.942. Para o fechamento do mês
// o export precisa mostrar a carteira inteira, então o que não está gravado é
// derivado aqui pela mesma fonte única (politicaComercial) que o modal Alterar
// Plano usa, e cada bloco sai acompanhado da sua PROCEDÊNCIA: quem lê a planilha
// tem que conseguir separar o que foi acordado do que foi calculado agora.
import { UserRole } from '../../../../types';
import {
    Publico,
    PlanoComercial,
    ModalidadePlano,
    PUBLICOS,
    classificarPublico,
    getPlano,
    inferirPlanoHistorico,
    getFatorFidelidade,
    getMultiplicadorPlano,
    isCarenciaSolicitada,
    resolverModalidadePlano,
} from '../../../../src/lib/politicaComercial';

export type Procedencia = 'gravado' | 'derivado' | 'inferido' | 'sem_plano' | 'indisponivel' | 'nao_aplica';

export const PROCEDENCIA_LABEL: Record<Procedencia, string> = {
    gravado: 'Gravado na negociação',
    derivado: 'Calculado no export',
    inferido: 'Inferido pelos % da UC',
    // Maior bucket da carteira antiga (333 negociações em ago/2026, quase todas
    // em 20%/50%): tem percentual acordado, mas nenhum plano da política vigente
    // no fechamento dela. Sem multiplicador — não havia multiplicador combinado.
    sem_plano: 'Condição anterior à política (sem plano)',
    indisponivel: 'Sem condição cadastrada',
    nao_aplica: 'Não se aplica (parceiro)',
};

export interface UcCondicao {
    desconto_tarifa?: number | string | null;
    desconto_bandeira?: number | string | null;
    carencia?: string | null;
    consumo_medio_kwh?: number | string | null;
}

export interface NegCondicao {
    consumo_medio_kwh?: number | string | null;
    fidelidade?: string | null;
    plano_codigo?: string | null;
    plano_modalidade?: string | null;
    publico?: string | null;
    multiplicador_plano?: number | string | null;
    fator_fidelidade?: number | string | null;
    multiplicador_final?: number | string | null;
}

export interface CondicaoComercial {
    modalidade: ModalidadePlano;
    /** Soma do consumo das UCs cadastradas. */
    volumeUcsKwh: number;
    /** Volume que classificou o público (UCs quando há, senão o da negociação). */
    volumeConsiderado: number;
    publico: Publico;
    publicoFaixa: string;
    publicoProcedencia: Procedencia;
    plano: PlanoComercial | null;
    planoProcedencia: Procedencia;
    /** O que exibir na coluna "Plano de desconto": o rótulo do plano quando há
     *  um, senão os percentuais acordados — célula vazia esconderia condição real. */
    planoDescricao: string;
    /** Desconto de fato gravado nas UCs, em pontos percentuais. */
    descTarifaPct: number | null;
    descBandeiraPct: number | null;
    /** UCs da mesma negociação com percentuais diferentes entre si. */
    descontosDivergentes: boolean;
    fidelidade: string;
    carencia: string;
    carenciaSolicitada: boolean;
    multPlano: number | null;
    fatorFidelidade: number | null;
    multFinal: number | null;
    multProcedencia: Procedencia;
}

const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const arred = (v: number, casas = 2) => {
    const f = 10 ** casas;
    return Math.round(v * f) / f;
};

/** A UC grava o desconto como fração (0,20 = 20%) — é assim que o PlanModal lê.
 *  Uma linha em produção guarda 100 na bandeira: acima de 1,5 o número já está
 *  em pontos percentuais e multiplicar de novo daria 10.000%. */
export const pctDesconto = (v: unknown): number | null => {
    const n = num(v);
    if (n === null) return null;
    return arred(n > 1.5 ? n : n * 100);
};

/** Primeiro valor não nulo entre as UCs — o PlanModal usa `ucs[0]`, mas uma UC
 *  sem desconto preenchido na frente da fila não pode zerar a negociação. */
const representativo = (ucs: UcCondicao[], campo: 'desconto_tarifa' | 'desconto_bandeira'): number | null => {
    for (const uc of ucs) {
        const p = pctDesconto(uc[campo]);
        if (p !== null) return p;
    }
    return null;
};

const divergem = (ucs: UcCondicao[]): boolean => {
    const chaves = new Set(
        ucs.map(u => `${representativo([u], 'desconto_tarifa') ?? '-'}/${representativo([u], 'desconto_bandeira') ?? '-'}`)
    );
    chaves.delete('-/-'); // UC sem nenhum percentual não conta como divergência
    return chaves.size > 1;
};

const carenciasDasUcs = (ucs: UcCondicao[]): string[] =>
    Array.from(new Set(ucs.map(u => (u.carencia || '').trim()).filter(Boolean)));

export function resolverCondicaoComercial(
    neg: NegCondicao,
    ucs: UcCondicao[],
    responsavelRole?: UserRole | null
): CondicaoComercial {
    const modalidade = resolverModalidadePlano({
        plano_modalidade: neg.plano_modalidade as ModalidadePlano | null | undefined,
        responsavel: responsavelRole ? { role: responsavelRole } : null,
    });

    const volumeUcsKwh = ucs.reduce((s, u) => s + (num(u.consumo_medio_kwh) ?? 0), 0);
    // recalcularCondicaoComercial() classifica pela soma das UCs; sem UC cadastrada
    // (ou com consumo zerado nelas) o único volume disponível é o da negociação.
    const volumeConsiderado = volumeUcsKwh > 0 ? volumeUcsKwh : (num(neg.consumo_medio_kwh) ?? 0);

    const publicoGravado =
        neg.publico === 'A' || neg.publico === 'B' || neg.publico === 'C' ? (neg.publico as Publico) : null;
    const publico = publicoGravado ?? classificarPublico(volumeConsiderado);

    const descTarifaPct = representativo(ucs, 'desconto_tarifa');
    const descBandeiraPct = representativo(ucs, 'desconto_bandeira');

    const planoGravado = getPlano(neg.plano_codigo);
    // Mesma inferência do PlanModal e do motor da folha: regra velha para
    // negociação velha — os planos criados na virada ficam de fora, senão a
    // carteira antiga herdaria um multiplicador que nunca foi acordado.
    const planoInferido =
        planoGravado || descTarifaPct === null
            ? null
            : inferirPlanoHistorico(Math.round(descTarifaPct), Math.round(descBandeiraPct ?? 0));
    const plano = planoGravado ?? planoInferido;
    // Sem plano mas com percentual gravado: a condição existe e tem que aparecer.
    const planoProcedencia: Procedencia = planoGravado
        ? 'gravado'
        : planoInferido
          ? 'inferido'
          : descTarifaPct !== null
            ? 'sem_plano'
            : 'indisponivel';
    const planoDescricao =
        plano?.label ??
        (descTarifaPct !== null ? `${descTarifaPct}% tarifa · ${descBandeiraPct ?? 0}% bandeira` : '');

    const fidelidade = neg.fidelidade || '';
    // Sem carência gravada é o padrão comercial (isCarenciaSolicitada trata null
    // como "sem carência"), mas sem UC nenhuma não há o que afirmar.
    const carencias = carenciasDasUcs(ucs);
    const carencia = carencias.length > 0 ? carencias.join(' | ') : ucs.length > 0 ? 'sem carência' : '';
    // Testado UC a UC: a string juntada de UCs com carências diferentes contém
    // "sem carência" e faria isCarenciaSolicitada() negar uma carência que existe.
    const carenciaSolicitada = carencias.some(isCarenciaSolicitada);

    const base = {
        modalidade,
        volumeUcsKwh: arred(volumeUcsKwh),
        volumeConsiderado: arred(volumeConsiderado),
        publico,
        publicoFaixa: PUBLICOS[publico].faixaLabel,
        publicoProcedencia: (publicoGravado ? 'gravado' : 'derivado') as Procedencia,
        plano,
        planoProcedencia,
        planoDescricao,
        descTarifaPct,
        descBandeiraPct,
        descontosDivergentes: divergem(ucs),
        fidelidade,
        carencia,
        carenciaSolicitada,
    };

    // O que está gravado sempre vence: é o que foi acordado, e o export relata —
    // não recalcula. A negociação 3616 tem fator 1,00 com 36 meses de fidelidade
    // (a escala diria 1,20), e é 1,00 que tem de sair na planilha. Vale inclusive
    // para as 3 negociações marcadas como 'parceiro' que têm multiplicador gravado.
    const multGravado = num(neg.multiplicador_final);
    if (multGravado !== null) {
        return {
            ...base,
            multPlano: num(neg.multiplicador_plano),
            fatorFidelidade: num(neg.fator_fidelidade),
            multFinal: multGravado,
            multProcedencia: 'gravado',
        };
    }

    // Parceiro e vendedor parceiro trabalham no formulário simples de percentuais:
    // a remuneração vem da rede (comissaoExternaV2), não do multiplicador. DERIVAR
    // um aqui seria inventar número de comissão para quem não tem essa régua.
    if (modalidade === 'parceiro') {
        return { ...base, multPlano: null, fatorFidelidade: null, multFinal: null, multProcedencia: 'nao_aplica' };
    }

    // Sem plano não há multiplicador a apurar — repete a procedência do plano
    // para a planilha dizer POR QUE a célula está vazia.
    if (!plano) {
        return { ...base, multPlano: null, fatorFidelidade: null, multFinal: null, multProcedencia: planoProcedencia };
    }

    const multPlano = getMultiplicadorPlano(plano.codigo, publico);
    const fatorFidelidade = getFatorFidelidade(fidelidade, plano.codigo);
    return {
        ...base,
        multPlano,
        fatorFidelidade,
        multFinal: arred(multPlano * fatorFidelidade),
        multProcedencia: 'derivado',
    };
}
